/**
 * Event types for the HistoryTab component
 * Each event type has its own specific fields
 */

// Base event type with common fields
export interface BaseHistoryEvent {
  id: string
  date: string | Date
  type: 'permit' | 'transaction' | 'rental' | 'sale'
}

// Building Permit Event
export interface PermitEvent extends BaseHistoryEvent {
  id: string
  type: 'permit'
  permitNumber?: string
  workDescription: string
  workType?: string
  owner?: string
  architect?: string
  address?: string
  result?: string // approved, pending, rejected
  projectValue?: number
  projectArea?: number
  classification?: string
  inquiryStartDate?: string
  inquiryLastDate?: string
  participantsNumber?: number
  developmentPotential?: string
}

// Transaction Event - now uses the Transaction type from @popety-io/types
export interface TransactionEvent extends BaseHistoryEvent {
  type: 'transaction'
  // Core transaction data from @popety-io/types
  transaction_id: string
  official_id?: string
  transaction_date?: string
  publication_date?: string
  transaction_total_price?: number
  transaction_step_total_price?: number
  transaction_step_name?: string
  transaction_step_text?: string
  buyers_count: number
  sellers_count: number
  properties_count: number
  transaction_participants: Array<{
    id: string
    role: string
    full_name: string
    type?: string
  }>
  transaction_properties: Array<{
    id: string
    city_name?: string
    npa_name?: string
    surface_area_total?: number
    land_area?: number
  }>
}

// Rental Listing Event
export interface ListingEvent extends BaseHistoryEvent {
  type: 'rental' | 'sale'
  listingId?: number // Numeric API ID for fetching full detail
  price: number
  pricePerSqm?: number
  address?: string
  propertyType?: string
  propertyCategory?: string
  area?: number
  rooms?: number
  bathrooms?: number
  landArea?: number
  floor?: number
  constructionYear?: number
  description?: string
  status: 'Active' | 'Inactive'
  ad_status: boolean
  onlineDays?: number
  imageUrls?: string[]
  brokerName?: string
  brokersInfo?: {
    contact_names?: {
      name: string
      type: string
    }[]
    mandate?: string
    partner_names?: string
    type?: string
  }
  brokerCompany?: string
  availableDate?: string
  features?: string[]
  furnished?: boolean
  petsAllowed?: boolean
  parking?: boolean
  balcony?: boolean
  terrace?: boolean
  garden?: boolean
  garage?: boolean
  elevator?: boolean
  renovationYear?: number
  energyLabel?: string
  heatingType?: string
}

// Union type for all events
export type HistoryEvent = PermitEvent | TransactionEvent | ListingEvent

// Type guards
export const isPermitEvent = (event: HistoryEvent): event is PermitEvent =>
  event.type === 'permit'

export const isTransactionEvent = (
  event: HistoryEvent
): event is TransactionEvent => event.type === 'transaction'

export const isRentalEvent = (event: HistoryEvent): event is ListingEvent =>
  event.type === 'rental'

export const isSaleEvent = (event: HistoryEvent): event is ListingEvent =>
  event.type === 'sale'
